import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import {
  Box, Stack, Group, Text, TextInput, Select, Autocomplete, Textarea, Button, Divider,
  UnstyledButton, Loader, ScrollArea, Modal,
} from '@mantine/core';
import { IconSearch, IconCar, IconUser, IconClipboardText, IconChevronLeft } from '@tabler/icons-react';
import { conversationsApi, vehicleApi, quoteApi, messagesApi } from '../api/client';
import type { ConversationResponse } from '../types';

const BG = '#0C0C0E';
const PANEL_BG = '#111113';
const CARD_BG = '#18181B';
const BORDER = '#27272A';
const TEXT = '#F4F4F5';
const TEXT_MUTED = '#71717A';
const BLUE = '#2563EB';

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA',
  'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT',
  'VA', 'WA', 'WV', 'WI', 'WY',
];

const inputStyles = {
  input: { background: '#0C0C0E', borderColor: BORDER, color: TEXT, fontSize: 14 },
  label: { color: '#A1A1AA', fontSize: 12, marginBottom: 4, fontWeight: 600 as const },
  // Autocomplete dropdowns (Year/Make/Model) share this — without these the
  // option text falls back to Mantine's defaults and renders nearly invisible
  // on the dark panel. Match the State <Select> below.
  dropdown: { background: '#1E1E21', border: `1px solid ${BORDER}` },
  option: { color: TEXT, fontSize: 13 },
};
const monoStyles = {
  input: { background: '#0C0C0E', borderColor: BORDER, color: TEXT, fontFamily: 'monospace', letterSpacing: 1, fontSize: 14 },
  label: { color: '#A1A1AA', fontSize: 12, marginBottom: 4, fontWeight: 600 as const },
};

function SectionCard({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <Box style={{ background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: 12, padding: 16 }}>
      <Group gap={8} mb={12}>
        {icon}
        <Text size="sm" fw={700} c={TEXT} tt="uppercase" style={{ letterSpacing: '0.06em' }}>{title}</Text>
      </Group>
      <Stack gap={12}>{children}</Stack>
    </Box>
  );
}

export function NewRoPage() {
  const navigate = useNavigate();

  // ── Vehicle ──
  const [plate, setPlate] = useState('');
  const [state, setState] = useState<string | null>(null);
  const [vin, setVin] = useState('');
  const [year, setYear] = useState('');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');

  // Cascading Year → Make → Model options from the EPA trims catalog (intake dropdowns).
  // Searchable suggestions only — the Autocompletes still accept any typed value, so a
  // vehicle not in the catalog is never blocked.
  // Only query YMM once a PLAUSIBLE 4-digit year is entered — typing "2" mid-entry
  // must not fire /ymm/makes?year=2 (the backend rejects out-of-range years with 422).
  const _yRaw = year.trim() && !Number.isNaN(Number(year)) ? Number(year) : null;
  const yNum = _yRaw != null && _yRaw >= 1900 && _yRaw <= new Date().getFullYear() + 2 ? _yRaw : null;
  const { data: yearOpts } = useSWR('ymm/years', () => vehicleApi.ymmYears());
  const { data: makeOpts } = useSWR(yNum ? ['ymm/makes', yNum] : null, () => vehicleApi.ymmMakes(yNum as number));
  const { data: modelOpts } = useSWR(
    yNum && make.trim() ? ['ymm/models', yNum, make.trim()] : null,
    () => vehicleApi.ymmModels(yNum as number, make.trim()),
  );
  const yearData = useMemo(() => (yearOpts || []).map(String), [yearOpts]);

  // ── Customer ──
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');

  // ── Intake ──
  const [intakeDate, setIntakeDate] = useState(() => {
    const d = new Date();
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  });
  const [mileage, setMileage] = useState('');
  const [concerns, setConcerns] = useState('');

  // ── Finder (single popup — finds an existing customer AND their vehicle) ──
  const [finderOpen, setFinderOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ConversationResponse[]>([]);
  const [searching, setSearching] = useState(false);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const runSearch = (q: string) => {
    setQuery(q);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    const trimmed = q.trim();
    if (trimmed.length < 2) { setResults([]); setSearching(false); return; }
    setSearching(true);
    searchTimer.current = setTimeout(async () => {
      try {
        const res = await conversationsApi.list({ search: trimmed, limit: 20 });
        // Only rows that carry a vehicle/customer are useful to prefill from —
        // and the picker exists to prefill, so collapse to one row per UNIQUE
        // vehicle/customer (the list is recency-ordered, so the kept row is the
        // most recent), not one row per past conversation.
        const usable = (res.conversations || []).filter((c) => c.vehicle && (c.vehicle.plate || c.vehicle.vin || c.vehicle.make || c.vehicle.first_name || c.vehicle.last_name));
        const seen = new Set<string>();
        setResults(usable.filter((c) => {
          const v = c.vehicle!;
          const key = (v.vin || v.plate || `${v.year}|${v.make}|${v.model}|${v.first_name || ''}|${v.last_name || ''}`).toString().toLowerCase();
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        }));
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
  };

  const fillFromVehicle = (conv: ConversationResponse) => {
    const v = conv.vehicle!;
    setPlate(v.plate || '');
    setState(v.state || null);
    setVin(v.vin || '');
    setYear(v.year != null ? String(v.year) : '');
    setMake(v.make || '');
    setModel(v.model || '');
    setFirstName(v.first_name || '');
    setLastName(v.last_name || '');
    setPhone(v.phone || '');
    setEmail(v.email || '');
    if (v.mileage != null) setMileage(String(v.mileage));
    setQuery('');
    setResults([]);
    setFinderOpen(false);
  };

  // Same customer/vehicle recurs once per past visit — collapse to the most
  // recent row per vehicle identity (VIN → plate → name+phone) so the finder
  // lists each customer/vehicle once.
  const dedupedResults = useMemo(() => {
    const seen = new Set<string>();
    const out: ConversationResponse[] = [];
    for (const c of results) {
      const v = c.vehicle!;
      const key = (v.vin || v.plate || `${v.first_name || ''}|${v.last_name || ''}|${v.phone || ''}`)
        .toString().toUpperCase().trim();
      if (key && seen.has(key)) continue;
      if (key) seen.add(key);
      out.push(c);
    }
    return out;
  }, [results]);

  const canCreate = useMemo(
    () => !!(plate.trim() || vin.trim() || make.trim()),
    [plate, vin, make],
  );

  const handleCreate = async () => {
    if (!canCreate || creating) return;
    const trimmedVin = vin.trim().toUpperCase();
    if (trimmedVin && trimmedVin.length !== 17) { setError('VIN must be exactly 17 characters.'); return; }
    setError('');
    setCreating(true);
    try {
      const payload: Parameters<typeof conversationsApi.create>[0] = {};
      // Explicit New-RO action — always start a FRESH conversation, even when
      // the vehicle already has an active one (never recall an old RO).
      payload.force_new = true;
      if (trimmedVin) payload.vin = trimmedVin;
      if (plate.trim()) payload.plate = plate.trim().toUpperCase();
      if (year.trim()) { const y = Number(year.trim()); if (!Number.isNaN(y)) payload.year = y; }
      if (make.trim()) payload.make = make.trim();
      if (model.trim()) payload.model = model.trim();
      const mi = Number(mileage.replace(/[^0-9]/g, ''));
      if (mi > 0) payload.mileage = mi;
      if (firstName.trim()) payload.first_name = firstName.trim();
      if (lastName.trim()) payload.last_name = lastName.trim();
      if (phone.trim()) payload.phone = phone.trim();
      if (email.trim()) payload.email = email.trim();

      const conv = await conversationsApi.create(payload);

      // state + intake_date + concerns aren't part of the create payload — set
      // them right after (best-effort; the RO already exists at this point).
      if (state) { try { await vehicleApi.patch(conv.id, { state }); } catch { /* non-critical */ } }
      if (intakeDate) { try { await conversationsApi.patch(conv.id, { intake_date: intakeDate } as any); } catch { /* non-critical */ } }
      if (concerns.trim()) { try { await quoteApi.upsert(conv.id, { items: [], description: concerns.trim() }); } catch { /* non-critical */ } }

      // Trigger the vehicle decode. create() only STORES vin/plate/ymm on the
      // vehicle row — it does NOT decode. The decode runs in the conversation
      // workflow (route_vehicle → lookup_vehicle, VehicleDatabases), which only
      // fires when a message carries the identifier. Send ONE identifier message,
      // priority VIN → plate → YMM (only what was entered). Fire-and-forget; the
      // lookup fills make/model/year (and VIN from plate) in the background.
      const ymm = [year.trim(), make.trim(), model.trim()].filter(Boolean).join(' ');
      let decodeMsg = '';
      if (trimmedVin) decodeMsg = `VIN: ${trimmedVin}`;
      else if (plate.trim()) decodeMsg = state ? `License plate: ${plate.trim().toUpperCase()}, State: ${state}` : `License plate: ${plate.trim().toUpperCase()}`;
      else if (ymm) decodeMsg = `Vehicle: ${ymm}`;
      if (decodeMsg) messagesApi.send({ conversation_id: conv.id, content: decodeMsg }).catch(() => {});

      navigate(`/shop/conversations/${conv.id}`);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to create. Try again.');
      setCreating(false);
    }
  };

  return (
    <Box style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', background: BG }}>
      <ScrollArea style={{ flex: 1, minHeight: 0 }} type="auto">
        <Box style={{ maxWidth: 820, margin: '0 auto', width: '100%', padding: '20px 16px 24px' }}>
          <Group gap={8} mb={16}>
            <UnstyledButton onClick={() => navigate(-1)} style={{ display: 'flex', alignItems: 'center', color: TEXT_MUTED }}>
              <IconChevronLeft size={18} />
            </UnstyledButton>
            <Text size="xl" fw={800} c={TEXT}>New RO</Text>
            <Text size="sm" c={TEXT_MUTED}>· nothing is saved until you press Create</Text>
          </Group>

          <Stack gap={14}>
            {/* Vehicle — find existing or create new */}
            <SectionCard icon={<IconCar size={16} color={BLUE} />} title="Vehicle">
              <Button
                fullWidth
                leftSection={<IconSearch size={16} />}
                onClick={() => setFinderOpen(true)}
                styles={{
                  root: { background: 'rgba(37,99,235,0.12)', border: '1px solid rgba(37,99,235,0.35)', color: '#93b4ff', height: 40 },
                  label: { fontWeight: 600 },
                }}
              >
                Find existing customer / vehicle
              </Button>

              <Divider label={<Text size="xs" c={TEXT_MUTED}>vehicle details</Text>} labelPosition="center" color={BORDER} />

              <Group gap={8} wrap="nowrap" align="flex-end">
                <TextInput
                  label="Plate"
                  placeholder="ABC 1234"
                  value={plate}
                  onChange={(e) => setPlate(e.currentTarget.value.toUpperCase())}
                  autoComplete="off"
                  style={{ flex: 1 }}
                  styles={monoStyles}
                />
                <Select
                  label="State"
                  placeholder="CA"
                  data={US_STATES}
                  value={state}
                  onChange={setState}
                  w={84}
                  searchable
                  clearable
                  comboboxProps={{ withinPortal: true }}
                  styles={{
                    input: { background: '#0C0C0E', borderColor: BORDER, color: TEXT, fontSize: 13 },
                    label: { color: '#A1A1AA', fontSize: 12, marginBottom: 4, fontWeight: 600 },
                    dropdown: { background: '#1E1E21', border: `1px solid ${BORDER}` },
                    option: { color: TEXT, fontSize: 13 },
                  }}
                />
              </Group>
              <TextInput
                label="VIN"
                placeholder="1HGBH41JXMN109186"
                value={vin}
                onChange={(e) => setVin(e.currentTarget.value.toUpperCase())}
                autoComplete="off"
                maxLength={17}
                description={vin.length > 0 ? `${vin.length}/17` : undefined}
                styles={monoStyles}
              />
              <Group gap={8} wrap="nowrap" align="flex-start">
                <Autocomplete
                  label="Year" placeholder="2024" w={84} autoComplete="off" styles={inputStyles} maxDropdownHeight={240}
                  data={yearData} value={year}
                  onChange={(val) => { setYear(val.replace(/[^0-9]/g, '').slice(0, 4)); setMake(''); setModel(''); }}
                />
                <Autocomplete
                  label="Make" placeholder="Toyota" style={{ flex: 1 }} autoComplete="off" styles={inputStyles} maxDropdownHeight={240}
                  data={makeOpts || []} value={make}
                  onChange={(val) => { setMake(val); setModel(''); }}
                />
                <Autocomplete
                  label="Model" placeholder="Camry" style={{ flex: 1 }} autoComplete="off" styles={inputStyles} maxDropdownHeight={240}
                  data={modelOpts || []} value={model}
                  onChange={setModel}
                />
              </Group>
            </SectionCard>

            {/* Customer — find-or-create */}
            <SectionCard icon={<IconUser size={16} color={BLUE} />} title="Customer">
              <Group gap={8} wrap="nowrap">
                <TextInput label="First name" value={firstName} onChange={(e) => setFirstName(e.currentTarget.value)} autoComplete="off" style={{ flex: 1 }} styles={inputStyles} />
                <TextInput label="Last name" value={lastName} onChange={(e) => setLastName(e.currentTarget.value)} autoComplete="off" style={{ flex: 1 }} styles={inputStyles} />
              </Group>
              <Group gap={8} wrap="nowrap">
                <TextInput label="Phone" value={phone} onChange={(e) => setPhone(e.currentTarget.value)} autoComplete="off" style={{ flex: 1 }} styles={inputStyles} />
                <TextInput label="Email" value={email} onChange={(e) => setEmail(e.currentTarget.value)} autoComplete="off" style={{ flex: 1 }} styles={inputStyles} />
              </Group>
            </SectionCard>

            {/* Intake */}
            <SectionCard icon={<IconClipboardText size={16} color={BLUE} />} title="Intake">
              <Box>
                <Text size="xs" fw={600} c="#A1A1AA" mb={4}>Date</Text>
                <input
                  type="datetime-local"
                  value={intakeDate}
                  onChange={(e) => setIntakeDate(e.currentTarget.value)}
                  style={{ background: '#0C0C0E', border: `1px solid ${BORDER}`, borderRadius: 6, color: TEXT, fontSize: 14, padding: '8px 10px', colorScheme: 'dark' }}
                />
              </Box>
              <TextInput label="Mileage" placeholder="12312" value={mileage} onChange={(e) => setMileage(e.currentTarget.value.replace(/[^0-9]/g, ''))} autoComplete="off" styles={inputStyles} />
              <Box>
                <Text size="xs" fw={600} c="#A1A1AA" mb={4}>Concerns</Text>
                <Textarea
                  placeholder="Customer concerns…"
                  value={concerns}
                  onChange={(e) => setConcerns(e.currentTarget.value)}
                  autosize
                  minRows={2}
                  maxRows={8}
                  styles={{ input: { background: '#0C0C0E', borderColor: BORDER, color: TEXT, fontSize: 14, lineHeight: 1.5 } }}
                />
              </Box>
            </SectionCard>

            {error && <Text size="sm" c="red">{error}</Text>}
          </Stack>
        </Box>
      </ScrollArea>

      {/* Footer — Create lives in the bottom-right corner. */}
      <Box style={{ flexShrink: 0, borderTop: `1px solid ${BORDER}`, background: PANEL_BG, padding: '12px 16px' }}>
        <Group justify="space-between" style={{ maxWidth: 820, margin: '0 auto' }}>
          <Button variant="subtle" color="gray" onClick={() => navigate(-1)}>Cancel</Button>
          <Button
            onClick={handleCreate}
            disabled={!canCreate}
            loading={creating}
            size="md"
            style={{ background: canCreate ? BLUE : '#2A2A2E', color: canCreate ? '#fff' : TEXT_MUTED, minWidth: 140 }}
          >
            Create
          </Button>
        </Group>
      </Box>

      {/* Unified finder — one popup that locates an existing customer AND their
          vehicle (search hits plate / VIN / name / phone) and prefills the whole
          form on select. */}
      <Modal
        opened={finderOpen}
        onClose={() => { setFinderOpen(false); setQuery(''); setResults([]); }}
        title={<Group gap={8}><IconSearch size={16} color={BLUE} /><Text fw={700} c={TEXT}>Find customer / vehicle</Text></Group>}
        centered
        size="lg"
        styles={{
          content: { background: PANEL_BG, border: `1px solid ${BORDER}` },
          header: { background: PANEL_BG, borderBottom: `1px solid ${BORDER}` },
          close: { color: TEXT_MUTED },
        }}
      >
        <Stack gap={10} pt={4}>
          <TextInput
            placeholder="Search by plate, VIN, name, or phone…"
            value={query}
            onChange={(e) => runSearch(e.currentTarget.value)}
            leftSection={searching ? <Loader size={14} /> : <IconSearch size={15} color={TEXT_MUTED} />}
            autoFocus
            autoComplete="off"
            styles={inputStyles}
          />
          {query.trim().length >= 2 && !searching && dedupedResults.length === 0 ? (
            <Text size="sm" c={TEXT_MUTED} ta="center" py="lg">No matching customer or vehicle.</Text>
          ) : (
            <ScrollArea.Autosize mah={360} type="auto">
              <Stack gap={6}>
                {dedupedResults.map((conv) => {
                  const v = conv.vehicle!;
                  const veh = [v.year, v.make, v.model].filter(Boolean).join(' ') || 'Unknown vehicle';
                  const who = [v.first_name, v.last_name].filter(Boolean).join(' ');
                  const contact = [v.phone, v.email].filter(Boolean).join(' · ');
                  const sub = [who || 'No customer name', contact].filter(Boolean).join(' · ');
                  return (
                    <UnstyledButton
                      key={conv.id}
                      onClick={() => fillFromVehicle(conv)}
                      style={{ padding: '10px 12px', borderRadius: 8, border: `1px solid ${BORDER}`, background: CARD_BG, display: 'block' }}
                      onMouseEnter={(e) => (e.currentTarget.style.borderColor = BLUE)}
                      onMouseLeave={(e) => (e.currentTarget.style.borderColor = BORDER)}
                    >
                      <Group justify="space-between" wrap="nowrap" gap={10}>
                        <Box style={{ minWidth: 0 }}>
                          <Text size="sm" fw={600} c={TEXT} truncate>{veh}</Text>
                          <Text size="xs" c={TEXT_MUTED} truncate>{sub}</Text>
                        </Box>
                        {v.plate && (
                          <Text size="xs" c={TEXT_MUTED} style={{ fontFamily: 'monospace', letterSpacing: 1, flexShrink: 0 }}>{v.plate}</Text>
                        )}
                      </Group>
                    </UnstyledButton>
                  );
                })}
              </Stack>
            </ScrollArea.Autosize>
          )}
        </Stack>
      </Modal>
    </Box>
  );
}
